import React, { useCallback, useMemo } from 'react';
import { Text, Select, SelectOptionProps, Box, HStack, TextSize } from '@televet/kibble-ui';
import { format } from 'date-fns';
import { FormControlSize } from '@televet/kibble-ui/build/shared/types/formControl';

// The time values are using moment syntax, whereas the labels use the date-fns syntax
// Moment was removed from the front end in favor of date-fns because of its size
export enum TimeFormatValue {
  Time = 'h:mm a',
  Day = 'dddd',
  DayAndTime = 'dddd [at] h:mma z',
  DayAndDate = 'dddd[,] MMMM Do',
  DayAndDateAndYear = 'dddd[,] MMMM Do[,] YYYY',
  DayAndDateAndTime = 'dddd[,] MMMM Do [at] h:mma z',
  DayAndDateAndTimeAndYear = 'dddd[,] MMMM Do[,] YYYY [at] h:mma z',
}

export interface WildcardTimeFormatSelectProps {
  timeFormatValue?: TimeFormatValue;
  selectSize?: FormControlSize;
  fontSize?: TextSize;
  onChange?: (selectedOption: SelectOptionProps) => void;
}

const WildcardTimeFormatSelect = ({
  timeFormatValue,
  selectSize = 'sm',
  fontSize = 'sm',
  onChange,
}: WildcardTimeFormatSelectProps): JSX.Element | null => {
  const dateFormatOptions = useMemo(() => {
    const date = new Date();
    return [
      { value: TimeFormatValue.Time, label: format(date, 'h:mm aaa') },
      { value: TimeFormatValue.Day, label: format(date, 'iiii') },
      { value: TimeFormatValue.DayAndTime, label: format(date, "iiii 'at' h:mm aaa 'CDT'") },
      { value: TimeFormatValue.DayAndDate, label: format(date, "iiii',' MMMM co") },
      { value: TimeFormatValue.DayAndDateAndYear, label: format(date, "iiii',' MMMM co',' y") },
      { value: TimeFormatValue.DayAndDateAndTime, label: format(date, "iiii',' MMMM co 'at' h:mm aaa 'CDT'") },
      {
        value: TimeFormatValue.DayAndDateAndTimeAndYear,
        label: format(date, "iiii',' MMMM co',' y 'at' h:mm aaa 'CDT'"),
      },
    ];
  }, []);

  const handleChange = useCallback(
    (selectedOption: SelectOptionProps) => {
      if (onChange) {
        onChange(selectedOption);
      }
    },
    [onChange],
  );

  return (
    <HStack>
      <Text size={fontSize}>Date&nbsp;Format:</Text>
      <Box minW="320px">
        <Select
          zIndex="1"
          listProps={{ onSelect: handleChange, minW: '100%' }}
          value={timeFormatValue}
          options={dateFormatOptions}
          size={selectSize}
        />
      </Box>
    </HStack>
  );
};

export default WildcardTimeFormatSelect;
